import { firestoreConstructors } from '@/plugins/firebase'

/** Sentinel value for the technician selector (not a real Firestore user id). */
export const PONCE_TECHNICIAN_SELECT_VALUE = '__ponce_technician__'

const USERS = 'app/v1/users'
const FIELD_SURVEYS = 'app/v1/fieldSurveys'
const SURVEY_WORKS = 'app/v1/surveyWorks'
const ORGANIZATIONS = 'app/v1/organizations'

/**
 * @param {Array} arr
 * @param {number} size
 * @returns {Array<Array>}
 */
function chunk (arr, size) {
  const out = []
  for (let i = 0; i < arr.length; i += size) {
    out.push(arr.slice(i, i + size))
  }
  return out
}

/**
 * @param {import('firebase').firestore.Firestore} db
 * @param {string} orgId
 * @param {import('firebase').firestore.DocumentReference} currentFieldSurveyRef
 * @returns {Promise<import('firebase').firestore.DocumentReference[]>}
 */
export async function getOrganizationFieldSurveyRefs (db, orgId, currentFieldSurveyRef) {
  const refs = []
  if (orgId) {
    const orgRef = db.collection(ORGANIZATIONS).doc(orgId)
    const snap = await db.collection(FIELD_SURVEYS).where('organization', '==', orgRef).get()
    snap.forEach((doc) => {
      refs.push(db.collection(FIELD_SURVEYS).doc(doc.id))
    })
  }
  if (refs.length === 0 && currentFieldSurveyRef) {
    refs.push(currentFieldSurveyRef)
  }
  const seen = new Set()
  return refs.filter((ref) => {
    const id = ref.id
    if (seen.has(id)) return false
    seen.add(id)
    return true
  })
}

/**
 * @param {import('firebase').firestore.Firestore} db
 * @param {string[]} userIds
 * @returns {Promise<Map<string, { name: string, lastName: string }>>}
 */
export async function fetchUsersByIds (db, userIds) {
  const map = new Map()
  const unique = [...new Set(userIds.filter(Boolean))]
  const batches = chunk(unique, 10)
  for (const batch of batches) {
    const snap = await db.collection(USERS).where(firestoreConstructors.FieldPath.documentId(), 'in', batch).get()
    snap.forEach((doc) => {
      const d = doc.data() || {}
      map.set(doc.id, {
        name: d.name != null ? String(d.name) : '',
        lastName: d.lastName != null ? String(d.lastName) : ''
      })
    })
  }
  return map
}

export function formatTechnicianDisplayName (name, lastName) {
  const a = String(name || '').trim()
  const b = String(lastName || '').trim()
  const full = [a, b].filter(Boolean).join(' ').trim()
  return full || a || b || '—'
}

/**
 * Returns the user document id from a Firestore DocumentReference to app/v1/users/{id}.
 * Does not return anything for plain strings (legacy) or invalid values — callers treat as no user.
 * Never return or store DocumentReference objects in Vue state (reactivity can overflow the stack).
 *
 * @param {unknown} raw
 * @returns {string|null}
 */
export function parseSurveyWorkUserRefId (raw) {
  if (raw == null) return null
  if (typeof raw === 'string') return null
  if (typeof raw !== 'object' || raw === null) return null
  const path = typeof raw.path === 'string' ? raw.path : ''
  const expectedPrefix = `${USERS}/`
  if (!path || !path.startsWith(expectedPrefix)) return null
  const id = typeof raw.id === 'string' ? raw.id : path.slice(expectedPrefix.length).split('/')[0]
  if (!id || id.length > 128) return null
  return id
}

/**
 * Loads DM users (fieldSurveys ∩ org field surveys) and technicians referenced on survey works.
 * @param {import('firebase').firestore.Firestore} db
 * @param {string} organizationId
 * @param {import('firebase').firestore.DocumentReference} currentFieldSurveyRef
 * @returns {Promise<Array<{ value: string, text: string }>>}
 */
export async function loadTechnicianSelectOptions (db, organizationId, currentFieldSurveyRef) {
  const fieldSurveyRefs = await getOrganizationFieldSurveyRefs(db, organizationId, currentFieldSurveyRef)
  if (fieldSurveyRefs.length === 0) {
    return []
  }

  const idSet = new Set()

  const dmBatches = chunk(fieldSurveyRefs, 10)
  for (const batch of dmBatches) {
    const snap = await db.collection(USERS).where('fieldSurveys', 'array-contains-any', batch).get()
    snap.forEach((doc) => idSet.add(doc.id))
  }

  const swBatches = chunk(fieldSurveyRefs, 10)
  for (const batch of swBatches) {
    const snap = await db.collection(SURVEY_WORKS).where('fieldSurvey', 'in', batch).get()
    snap.forEach((doc) => {
      const u = (doc.data() || {}).user
      const uid = parseSurveyWorkUserRefId(u)
      if (uid) idSet.add(uid)
    })
  }

  const userMap = await fetchUsersByIds(db, [...idSet])
  const items = []
  userMap.forEach((data, id) => {
    items.push({
      value: id,
      text: formatTechnicianDisplayName(data.name, data.lastName)
    })
  })
  items.sort((a, b) => a.text.localeCompare(b.text, undefined, { sensitivity: 'base' }))
  return items
}

/**
 * @param {string} userId
 * @param {string} name
 * @param {string} lastName
 * @param {Array<{ value: string, text: string }>} items
 * @returns {Array<{ value: string, text: string }>}
 */
export function mergeTechnicianOption (items, userId, name, lastName) {
  const text = formatTechnicianDisplayName(name, lastName)
  const next = items.filter((x) => x.value !== userId)
  next.push({ value: userId, text })
  next.sort((a, b) => a.text.localeCompare(b.text, undefined, { sensitivity: 'base' }))
  return next
}



// WEBPACK FOOTER //
// ./src/lib/surveyWorkTechnicians.js